var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};

// src/index.ts
import { registerWallet } from "@aptos-labs/wallet-standard";

// src/AptosConnectGoogleWallet.ts
import {
  AptosConnectNamespace,
  AptosDisconnectNamespace,
  AptosGetAccountNamespace,
  AptosGetNetworkNamespace,
  AptosOnAccountChangeNamespace,
  AptosOnNetworkChangeNamespace,
  AptosSignAndSubmitTransactionNamespace,
  AptosSignMessageNamespace,
  AptosSignTransactionNamespace
} from "@aptos-labs/wallet-standard";

// src/AptosConnectWallet.ts
import {
  AccountAddress as AccountAddress2,
  Aptos,
  AptosConfig,
  Ed25519PrivateKey,
  MultiAgentTransaction,
  Network as Network2,
  NetworkToNodeAPI,
  SimpleTransaction
} from "@aptos-labs/ts-sdk";
import {
  AccountInfo as AccountInfo3,
  APTOS_CHAINS as APTOS_CHAINS2,
  AptosWalletError,
  AptosWalletErrorCode,
  UserResponseStatus as UserResponseStatus2
} from "@aptos-labs/wallet-standard";
import { deserializePublicKeyB64, serializePublicKeyB64 } from "@identity-connect/crypto";
import { ACDappClient } from "@identity-connect/dapp-sdk";

// src/AptosConnectAccount.ts
import {
  AnyPublicKey,
  Ed25519PublicKey,
  MultiEd25519PublicKey,
  MultiKey,
  SigningScheme
} from "@aptos-labs/ts-sdk";
import { APTOS_CHAINS } from "@aptos-labs/wallet-standard";
var _address, _publicKey;
var AptosConnectAccount = class {
  constructor({ address, ansName, publicKey }) {
    // region AptosWalletAccount
    this.chains = APTOS_CHAINS;
    this.features = [];
    // endregion
    // region PetraAccount
    __privateAdd(this, _address, void 0);
    __privateAdd(this, _publicKey, void 0);
    __privateSet(this, _publicKey, publicKey);
    __privateSet(this, _address, address);
    this.label = ansName;
  }
  get address() {
    return __privateGet(this, _address).toString();
  }
  get publicKey() {
    return __privateGet(this, _publicKey).toUint8Array();
  }
  get signingScheme() {
    if (__privateGet(this, _publicKey) instanceof Ed25519PublicKey) {
      return SigningScheme.Ed25519;
    }
    if (__privateGet(this, _publicKey) instanceof MultiEd25519PublicKey) {
      return SigningScheme.MultiEd25519;
    }
    if (__privateGet(this, _publicKey) instanceof AnyPublicKey) {
      return SigningScheme.SingleKey;
    }
    if (__privateGet(this, _publicKey) instanceof MultiKey) {
      return SigningScheme.MultiKey;
    }
    throw new Error("Unsupported public key type");
  }
  // endregion
};
_address = new WeakMap();
_publicKey = new WeakMap();

// src/helpers.ts
import { Network, NetworkToChainId } from "@aptos-labs/ts-sdk";
import { AccountInfo as AccountInfo2, UserResponseStatus } from "@aptos-labs/wallet-standard";
function customAccountToStandardAccount({ address, name, publicKey }) {
  return new AccountInfo2({
    address,
    ansName: name,
    publicKey
  });
}
function unwrapUserResponse(response, callback) {
  if (response.status === "dismissed") {
    return { status: UserResponseStatus.REJECTED };
  }
  return { args: callback(response.args), status: UserResponseStatus.APPROVED };
}
function networkToChainId(network) {
  switch (network) {
    case Network.MAINNET:
      return NetworkToChainId.mainnet;
    case Network.TESTNET:
      return NetworkToChainId.testnet;
    default:
      return NetworkToChainId.devnet;
  }
}

// src/AptosConnectWallet.ts
var _AptosConnectWallet = class _AptosConnectWallet {
  constructor({
    claimSecretKey,
    network = Network2.MAINNET,
    preferredWalletName,
    ...clientConfig
  }) {
    // endregion
    // region AptosWallet
    this.version = "1.0.0";
    this.chains = APTOS_CHAINS2;
    this.client = new ACDappClient(clientConfig);
    if (!NetworkToNodeAPI[network]) {
      throw new Error("Network not supported");
    }
    const aptosConfig = new AptosConfig({ network });
    this.aptosClient = new Aptos(aptosConfig);
    this.preferredWalletName = preferredWalletName;
    this.claimOptions = claimSecretKey ? { network, secretKey: new Ed25519PrivateKey(claimSecretKey) } : void 0;
  }
  static get connectedAccount() {
    const serialized = localStorage.getItem(_AptosConnectWallet.connectedAccountStorageKey);
    if (!serialized) {
      return void 0;
    }
    try {
      const { address, publicKey } = JSON.parse(serialized);
      return new AccountInfo3({
        address: AccountAddress2.from(address),
        publicKey: deserializePublicKeyB64(publicKey)
      });
    } catch (err) {
      console.warn("Inconsistent state, resetting it");
      this.connectedAccount = void 0;
      return void 0;
    }
  }
  static set connectedAccount(value) {
    if (value !== void 0) {
      const serialized = {
        address: value.address.toString(),
        publicKey: serializePublicKeyB64(value.publicKey)
      };
      localStorage.setItem(_AptosConnectWallet.connectedAccountStorageKey, JSON.stringify(serialized));
    } else {
      localStorage.removeItem(_AptosConnectWallet.connectedAccountStorageKey);
    }
  }
  // eslint-disable-next-line class-methods-use-this
  get accounts() {
    const { connectedAccount } = _AptosConnectWallet;
    return connectedAccount ? [new AptosConnectAccount(connectedAccount)] : [];
  }
  async connect() {
    const { connectedAccount } = _AptosConnectWallet;
    if (connectedAccount !== void 0) {
      return { args: connectedAccount, status: UserResponseStatus2.APPROVED };
    }
    const response = await this.client.connect({
      claimOptions: this.claimOptions,
      preferredWalletName: this.preferredWalletName
    });
    if (response.status === "dismissed") {
      return { status: UserResponseStatus2.REJECTED };
    }
    const newConnectedAccount = customAccountToStandardAccount(response.args.account);
    _AptosConnectWallet.connectedAccount = newConnectedAccount;
    return {
      args: newConnectedAccount,
      status: UserResponseStatus2.APPROVED
    };
  }
  async disconnect() {
    const { connectedAccount } = _AptosConnectWallet;
    if (connectedAccount) {
      await this.client.disconnect(connectedAccount.address);
      _AptosConnectWallet.connectedAccount = void 0;
    }
  }
  // eslint-disable-next-line class-methods-use-this
  async getAccount() {
    const { connectedAccount } = _AptosConnectWallet;
    if (!connectedAccount) {
      throw new AptosWalletError(AptosWalletErrorCode.Unauthorized);
    }
    return customAccountToStandardAccount(connectedAccount);
  }
  async getNetwork() {
    const { network } = this.aptosClient.config;
    const chainId = await this.aptosClient.getChainId();
    const url = NetworkToNodeAPI[network];
    return {
      chainId,
      name: network,
      url
    };
  }
  async signMessage(input) {
    const { connectedAccount } = _AptosConnectWallet;
    if (!connectedAccount) {
      throw new AptosWalletError(AptosWalletErrorCode.Unauthorized);
    }
    const chainId = networkToChainId(this.aptosClient.config.network);
    const { message, nonce } = input;
    const encoder = new TextEncoder();
    const messageBytes = encoder.encode(message);
    const nonceBytes = encoder.encode(nonce);
    const response = await this.client.signMessage({
      chainId,
      message: messageBytes,
      nonce: nonceBytes,
      signerAddress: connectedAccount.address
    });
    if (response.status === "dismissed") {
      return { status: UserResponseStatus2.REJECTED };
    }
    const { fullMessage, signature } = response.args;
    const extraResponseArgs = {
      address: connectedAccount.address.toString(),
      application: this.client.dappInfo.domain,
      chainId,
      message,
      nonce,
      prefix: "APTOS"
    };
    return {
      args: {
        fullMessage,
        signature,
        ...extraResponseArgs
      },
      status: UserResponseStatus2.APPROVED
    };
  }
  async signTransaction(txnOrArgs, _asFeePayer) {
    const { connectedAccount } = _AptosConnectWallet;
    if (!connectedAccount) {
      throw new AptosWalletError(AptosWalletErrorCode.Unauthorized);
    }
    if ("bcsToBytes" in txnOrArgs) {
      const transaction = txnOrArgs;
      const feePayer = transaction.feePayerAddress ? { address: transaction.feePayerAddress } : void 0;
      const secondarySigners = transaction.secondarySignerAddresses?.map((address) => ({ address }));
      const response2 = await this.client.signTransaction({
        feePayer,
        secondarySigners,
        signerAddress: connectedAccount.address,
        transaction: transaction.rawTransaction
      });
      return unwrapUserResponse(response2, (args) => args.authenticator);
    }
    const requestArgs = txnOrArgs;
    const response = await this.client.signTransaction({
      ...requestArgs,
      signerAddress: connectedAccount.address
    });
    return unwrapUserResponse(response, (responseArgs) => {
      const { authenticator, rawTransaction } = responseArgs;
      if (!rawTransaction) {
        throw new Error("Expected raw transaction in response args");
      }
      const secondarySigners = requestArgs.secondarySigners ?? [];
      let transaction;
      if (secondarySigners.length > 0) {
        transaction = new MultiAgentTransaction(
          rawTransaction,
          secondarySigners.map((s) => s.address),
          requestArgs.feePayer?.address
        );
      } else {
        transaction = new SimpleTransaction(rawTransaction, requestArgs.feePayer?.address);
      }
      return {
        authenticator,
        rawTransaction: transaction
      };
    });
  }
  async signAndSubmitTransaction(args) {
    const { gasUnitPrice, maxGasAmount, payload } = args;
    const { connectedAccount } = _AptosConnectWallet;
    if (!connectedAccount) {
      throw new AptosWalletError(AptosWalletErrorCode.Unauthorized);
    }
    const response = await this.client.signAndSubmitTransaction({
      gasUnitPrice,
      maxGasAmount,
      network: this.aptosClient.config.network,
      payload,
      signerAddress: connectedAccount.address
    });
    if (response.status === "dismissed") {
      return { status: UserResponseStatus2.REJECTED };
    }
    return {
      args: { hash: response.args.txnHash },
      status: UserResponseStatus2.APPROVED
    };
  }
  // eslint-disable-next-line class-methods-use-this
  async onAccountChange(_callback) {
  }
  // eslint-disable-next-line class-methods-use-this
  async onNetworkChange(_callback) {
  }
  // endregion
};
// region connectedAccount
_AptosConnectWallet.connectedAccountStorageKey = "@aptos-connect/connectedAccount";
var AptosConnectWallet = _AptosConnectWallet;

// src/config.ts
var walletMetadata = {
  apple: {
    icon: (
      // eslint-disable-next-line max-len
      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICAgIDxzdHlsZT4KICAgICAgICBwYXRoIHsKICAgICAgICAgICAgZmlsbDogYmxhY2s7CiAgICAgICAgfQoKICAgICAgICBAbWVkaWEgKHByZWZlcnMtY29sb3Itc2NoZW1lOiBkYXJrKSB7CiAgICAgICAgICAgIHBhdGggewogICAgICAgICAgICAgICAgZmlsbDogd2hpdGU7CiAgICAgICAgICAgIH0KICAgICAgICB9CiAgICA8L3N0eWxlPgogICAgPHBhdGgKICAgICAgICBkPSJNMjAuMzkzMiA4LjE4MjQyQzIwLjI1NzggOC4yOTA0MyAxNy44NjggOS42NzUzNyAxNy44NjggMTIuNzU0OUMxNy44NjggMTYuMzE2OCAyMC45MDkgMTcuNTc3IDIxIDE3LjYwODJDMjAuOTg2IDE3LjY4NSAyMC41MTY5IDE5LjMzMzkgMTkuMzk2NiAyMS4wMTQxQzE4LjM5NzcgMjIuNDkyNiAxNy4zNTQ1IDIzLjk2ODggMTUuNzY3NSAyMy45Njg4QzE0LjE4MDQgMjMuOTY4OCAxMy43NzIgMjMuMDIwNyAxMS45Mzk5IDIzLjAyMDdDMTAuMTU0NSAyMy4wMjA3IDkuNTE5NzIgMjQgOC4wNjgwNSAyNEM2LjYxNjM5IDI0IDUuNjAzNDkgMjIuNjMxOSA0LjQzODg5IDIwLjk1MTdDMy4wODk5MiAxOC45Nzg3IDIgMTUuOTEzNiAyIDEzLjAwNDVDMiA4LjMzODQzIDQuOTUwMDEgNS44NjM3OSA3Ljg1MzM0IDUuODYzNzlDOS4zOTYwMiA1Ljg2Mzc5IDEwLjY4MiA2LjkwNTQ5IDExLjY1MDUgNi45MDU0OUMxMi41NzI0IDYuOTA1NDkgMTQuMDEwMSA1LjgwMTM4IDE1Ljc2NTEgNS44MDEzOEMxNi40MzAzIDUuODAxMzggMTguODIwMiA1Ljg2Mzc5IDIwLjM5MzIgOC4xODI0MlpNMTQuOTMxOSAzLjgyNTk4QzE1LjY1NzggMi45NDAyOSAxNi4xNzEyIDEuNzExMzcgMTYuMTcxMiAwLjQ4MjQ0OEMxNi4xNzEyIDAuMzEyMDMxIDE2LjE1NzIgMC4xMzkyMTQgMTYuMTI2OSAwQzE0Ljk0NiAwLjA0NTYwNDYgMTMuNTQxIDAuODA4ODgxIDEyLjY5MzggMS44MTkzOEMxMi4wMjg2IDIuNTk3MDYgMTEuNDA3OCAzLjgyNTk4IDExLjQwNzggNS4wNzE3MUMxMS40MDc4IDUuMjU4OTMgMTEuNDM4MiA1LjQ0NjE0IDExLjQ1MjIgNS41MDYxNUMxMS41MjY4IDUuNTIwNTUgMTEuNjQ4MiA1LjUzNzM1IDExLjc2OTYgNS41MzczNUMxMi44MjkxIDUuNTM3MzUgMTQuMTYxOCA0LjgwNzY4IDE0LjkzMTkgMy44MjU5OFoiIC8+Cjwvc3ZnPg=="
    ),
    name: "Continue with Apple",
    url: "https://aptosconnect.app"
  },
  google: {
    icon: (
      // eslint-disable-next-line max-len
      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiBjbGFzcz0iaF8yMCB3XzIwIj48cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTIzLjU0IDEyLjc2MTNDMjMuNTQgMTEuOTQ1OSAyMy40NjY4IDExLjE2MTggMjMuMzMwOSAxMC40MDkxSDEyLjVWMTQuODU3NUgxOC42ODkxQzE4LjQyMjUgMTYuMjk1IDE3LjYxMjMgMTcuNTEyOSAxNi4zOTQzIDE4LjMyODRWMjEuMjEzOEgyMC4xMTA5QzIyLjI4NTUgMTkuMjExOCAyMy41NCAxNi4yNjM2IDIzLjU0IDEyLjc2MTNaIiBmaWxsPSIjNDI4NUY0Ij48L3BhdGg+PHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xMi40OTk1IDIzLjk5OThDMTUuNjA0NSAyMy45OTk4IDE4LjIwNzcgMjIuOTcgMjAuMTEwNCAyMS4yMTM3TDE2LjM5MzggMTguMzI4MkMxNS4zNjQgMTkuMDE4MiAxNC4wNDY3IDE5LjQyNTkgMTIuNDk5NSAxOS40MjU5QzkuNTA0MjUgMTkuNDI1OSA2Ljk2OTAyIDE3LjQwMyA2LjA2NDcgMTQuNjg0OEgyLjIyMjY2VjE3LjY2NDRDNC4xMTQ5MyAyMS40MjI4IDguMDA0MDIgMjMuOTk5OCAxMi40OTk1IDIzLjk5OThaIiBmaWxsPSIjMzRBODUzIj48L3BhdGg+PHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik02LjA2NTIzIDE0LjY4NTFDNS44MzUyMyAxMy45OTUxIDUuNzA0NTUgMTMuMjU4MSA1LjcwNDU1IDEyLjUwMDFDNS43MDQ1NSAxMS43NDIyIDUuODM1MjMgMTEuMDA1MSA2LjA2NTIzIDEwLjMxNTFWNy4zMzU1N0gyLjIyMzE4QzEuNDQ0MzIgOC44ODgwNyAxIDEwLjY0NDQgMSAxMi41MDAxQzEgMTQuMzU1OCAxLjQ0NDMyIDE2LjExMjIgMi4yMjMxOCAxNy42NjQ3TDYuMDY1MjMgMTQuNjg1MVoiIGZpbGw9IiNGQkJDMDUiPjwvcGF0aD48cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTEyLjQ5OTUgNS41NzM4NkMxNC4xODc5IDUuNTczODYgMTUuNzAzOCA2LjE1NDA5IDE2Ljg5NTYgNy4yOTM2NEwyMC4xOTQgMy45OTUyM0MxOC4yMDI0IDIuMTM5NTUgMTUuNTk5MiAxIDEyLjQ5OTUgMUM4LjAwNDAyIDEgNC4xMTQ5MyAzLjU3NzA1IDIuMjIyNjYgNy4zMzU0NUw2LjA2NDcgMTAuMzE1QzYuOTY5MDIgNy41OTY4MiA5LjUwNDI1IDUuNTczODYgMTIuNDk5NSA1LjU3Mzg2WiIgZmlsbD0iI0VBNDMzNSI+PC9wYXRoPjwvc3ZnPg=="
    ),
    name: "Continue with Google",
    url: "https://aptosconnect.app"
  }
};

// src/AptosConnectGoogleWallet.ts
var AptosConnectGoogleWallet = class extends AptosConnectWallet {
  constructor(config) {
    super({ ...config, provider: "google" });
    this.name = walletMetadata.google.name;
    this.icon = walletMetadata.google.icon;
    this.url = walletMetadata.google.url;
  }
  // region AptosWallet
  get features() {
    return {
      [AptosConnectNamespace]: {
        connect: this.connect.bind(this),
        version: "1.0.0"
      },
      [AptosDisconnectNamespace]: {
        disconnect: this.disconnect.bind(this),
        version: "1.0.0"
      },
      [AptosGetAccountNamespace]: {
        account: this.getAccount.bind(this),
        version: "1.0.0"
      },
      [AptosGetNetworkNamespace]: {
        network: this.getNetwork.bind(this),
        version: "1.0.0"
      },
      [AptosOnAccountChangeNamespace]: {
        onAccountChange: this.onAccountChange.bind(this),
        version: "1.0.0"
      },
      [AptosOnNetworkChangeNamespace]: {
        onNetworkChange: this.onNetworkChange.bind(this),
        version: "1.0.0"
      },
      [AptosSignAndSubmitTransactionNamespace]: {
        signAndSubmitTransaction: this.signAndSubmitTransaction.bind(this),
        version: "1.1.0"
      },
      [AptosSignMessageNamespace]: {
        signMessage: this.signMessage.bind(this),
        version: "1.0.0"
      },
      [AptosSignTransactionNamespace]: {
        signTransaction: this.signTransaction.bind(this),
        version: "1.1"
      }
    };
  }
  // endregion
};

// src/AptosConnectAppleWallet.ts
import {
  AptosConnectNamespace as AptosConnectNamespace2,
  AptosDisconnectNamespace as AptosDisconnectNamespace2,
  AptosGetAccountNamespace as AptosGetAccountNamespace2,
  AptosGetNetworkNamespace as AptosGetNetworkNamespace2,
  AptosOnAccountChangeNamespace as AptosOnAccountChangeNamespace2,
  AptosOnNetworkChangeNamespace as AptosOnNetworkChangeNamespace2,
  AptosSignAndSubmitTransactionNamespace as AptosSignAndSubmitTransactionNamespace2,
  AptosSignMessageNamespace as AptosSignMessageNamespace2,
  AptosSignTransactionNamespace as AptosSignTransactionNamespace2
} from "@aptos-labs/wallet-standard";
var AptosConnectAppleWallet = class extends AptosConnectWallet {
  constructor(config) {
    super({ ...config, provider: "apple" });
    this.name = walletMetadata.apple.name;
    this.icon = walletMetadata.apple.icon;
    this.url = walletMetadata.apple.url;
  }
  // region AptosWallet
  get features() {
    return {
      [AptosConnectNamespace2]: {
        connect: this.connect.bind(this),
        version: "1.0.0"
      },
      [AptosDisconnectNamespace2]: {
        disconnect: this.disconnect.bind(this),
        version: "1.0.0"
      },
      [AptosGetAccountNamespace2]: {
        account: this.getAccount.bind(this),
        version: "1.0.0"
      },
      [AptosGetNetworkNamespace2]: {
        network: this.getNetwork.bind(this),
        version: "1.0.0"
      },
      [AptosOnAccountChangeNamespace2]: {
        onAccountChange: this.onAccountChange.bind(this),
        version: "1.0.0"
      },
      [AptosOnNetworkChangeNamespace2]: {
        onNetworkChange: this.onNetworkChange.bind(this),
        version: "1.0.0"
      },
      [AptosSignAndSubmitTransactionNamespace2]: {
        signAndSubmitTransaction: this.signAndSubmitTransaction.bind(this),
        version: "1.1.0"
      },
      [AptosSignMessageNamespace2]: {
        signMessage: this.signMessage.bind(this),
        version: "1.0.0"
      },
      [AptosSignTransactionNamespace2]: {
        signTransaction: this.signTransaction.bind(this),
        version: "1.1"
      }
    };
  }
  // endregion
};

// src/index.ts
function registerAptosConnect(config = {}) {
  const googleWalletPlugin = new AptosConnectGoogleWallet(config);
  const appleWalletPlugin = new AptosConnectAppleWallet(config);
  registerWallet(googleWalletPlugin);
  registerWallet(appleWalletPlugin);
}
export {
  AptosConnectAccount,
  AptosConnectAppleWallet,
  AptosConnectGoogleWallet,
  AptosConnectWallet,
  registerAptosConnect
};
//# sourceMappingURL=index.mjs.map